export type TimelineItem = {
  time: string;
  text: string;
};

export type Persona = {
  name: string;
  role: string;
  note: string;
  avatar: string;
  tag: string;
};

export type Story = {
  id: string;
  title: string;
  excerpt: string;
  category: string;
  categorySlug: string;
  tags: string[];
  cover: string;
  author: string;
  date: string;
  readTime: string;
  heat: number;
  status: '追踪中' | '已完结' | '待核实';
  featured?: boolean;
  verdict: string;
  verdictLevel: '已确认' | '多方印证' | '存疑' | '暂无法确认';
  content: string[];
  timeline: TimelineItem[];
  personas: Persona[];
};

export const stories: Story[] = [
  {
    id: 'm-01',
    title: '深夜同框照刷屏后，这条时间线帮你理清到底谁先发的',
    excerpt:
      '一张被反复转发的深夜街拍，把两位当红艺人推上热搜。我们从发布时间、画面细节与双方动态三条线入手，还原照片最初出现的位置。',
    category: '事件追踪',
    categorySlug: 'event-track',
    tags: ['时间线', '同框照', '首发来源'],
    cover:
      'https://readdy.ai/api/search-image?query=stack%20of%20folded%20newspapers%20with%20a%20magnifying%20glass%20hovering%20above%2C%20warm%20cream%20paper%20background%2C%20tomato%20red%20amber%20and%20deep%20teal%20flat%20vector%20editorial%20illustration%2C%20subtle%20grain%2C%20minimal%20geometric%20shapes%2C%20no%20text&width=800&height=600&seq=xigua-cover-01&orientation=landscape',
    author: '瓜田编辑部',
    date: '2026-09-08',
    readTime: '6 分钟',
    heat: 9820,
    status: '追踪中',
    featured: true,
    verdict: '同框照片的来源已能对应到最早发布的账号，但两人关系仍缺少当事人确认。',
    verdictLevel: '多方印证',
    content: [
      '这条瓜的起点并不是热搜，而是一个粉丝向账号在凌晨发出的模糊街拍。照片里两个人物的着装与次日通告造型基本一致，因此被迅速认定为「同一天」。',
      '我们把三张流传最广的版本做了对比，发现转发链条上至少存在一次明显的裁切，被裁掉的部分恰好是同行的工作人员，这也是后续「单独约会」说法出现的原因。',
      '截至目前，双方工作室都只回应了行程问题，没有正面谈及关系。以现有材料看，可以确认的是照片真实存在且拍摄于同一天，无法确认的是两人之间是否超出合作关系。',
    ],
    timeline: [
      { time: '第 1 天 02:14', text: '粉丝向账号首发模糊街拍，配文仅一句「今晚好热闹」。' },
      { time: '第 1 天 09:40', text: '娱乐资讯号二次转发并做裁切，同行人员被裁出画面。' },
      { time: '第 2 天 11:20', text: '双方工作室先后回应行程，未回应关系传闻。' },
    ],
    personas: [
      {
        name: '当事人 · 男方',
        role: '演员 / 男主',
        note: '当晚结束拍摄后未直接返程，其工作室回应称是「剧组聚餐」。',
        avatar:
          'https://readdy.ai/api/search-image?query=minimal%20flat%20vector%20portrait%20silhouette%20of%20a%20male%20figure%20with%20warm%20cream%20background%2C%20tomato%20red%20and%20amber%20tones%2C%20geometric%20shapes%2C%20no%20facial%20features%2C%20no%20text&width=400&height=400&seq=xigua-persona-male&orientation=squarish',
        tag: '主角',
      },
      {
        name: '当事人 · 女方',
        role: '歌手 / 女主',
        note: '当周在附近有演出安排，画面中的外套与其后援会应援服相似。',
        avatar:
          'https://readdy.ai/api/search-image?query=minimal%20flat%20vector%20portrait%20silhouette%20of%20a%20female%20figure%20with%20warm%20cream%20background%2C%20amber%20gold%20and%20deep%20teal%20tones%2C%20geometric%20shapes%2C%20no%20facial%20features%2C%20no%20text&width=400&height=400&seq=xigua-persona-female&orientation=squarish',
        tag: '女主',
      },
    ],
  },
  {
    id: 'm-02',
    title: '被扒出的「小号」到底是本人吗？四个维度逐条拆给你看',
    excerpt:
      '一个注册仅三天的账号突然开始精准爆料，被网友认定为当事人小号。我们从注册时间、语气习惯、活跃时段和关注列表四个角度做了比对。',
    category: '小号鉴定',
    categorySlug: 'alt-account',
    tags: ['小号', '账号分析', '存疑'],
    cover:
      'https://readdy.ai/api/search-image?query=smartphone%20floating%20with%20stylized%20chat%20bubbles%20and%20a%20small%20watermelon%20slice%2C%20warm%20cream%20background%2C%20tomato%20red%20amber%20gold%20deep%20teal%20flat%20vector%20illustration%2C%20minimal%20geometric%20magazine%20style%2C%20no%20text&width=800&height=600&seq=xigua-cover-02&orientation=landscape',
    author: '鉴定组 · 小南',
    date: '2026-09-07',
    readTime: '5 分钟',
    heat: 7640,
    status: '待核实',
    verdict: '该账号与当事人的关联性目前只能算「存疑」，尚不足以认定为本人。',
    verdictLevel: '存疑',
    content: [
      '判断一个小号是否属于某位公众人物，最容易踩的坑是「语气像」。语气可以模仿，但一些技术特征很难伪造。',
      '我们比对后发现，该账号的注册时间恰好与事件发酵同步，活跃时段也和当事人公开行程吻合，这两点确实有可疑之处。但同时，它的关注列表里出现了大量与当事人毫无交集的营销号，这一点与「本人运营」的说法相冲突。',
      '综合来看，我们把它标注为「存疑」：不能排除是团队或粉丝运营的账号，也不排除是蹭热度的营销号，需要等待更多材料。',
    ],
    timeline: [
      { time: '第 1 天 21:05', text: '账号首次发言，内容指向尚未公开的档期信息。' },
      { time: '第 2 天 13:30', text: '网友将其与当事人语气比对，话题冲上榜单。' },
      { time: '第 3 天 10:00', text: '账号发布一条否认营业的动态后停止更新。' },
    ],
    personas: [
      {
        name: '疑似关联小号',
        role: '匿名账号',
        note: '注册时间短、发言精准，但关注列表与当事人无交集。',
        avatar:
          'https://readdy.ai/api/search-image?query=minimal%20flat%20vector%20abstract%20avatar%20of%20an%20anonymous%20masked%20profile%20icon%2C%20warm%20cream%20background%2C%20tomato%20red%20and%20teal%20tones%2C%20geometric%20shapes%2C%20no%20text&width=400&height=400&seq=xigua-persona-anon&orientation=squarish',
        tag: '小号',
      },
      {
        name: '当事人 · 男方',
        role: '演员 / 男主',
        note: '未对该账号做出任何回应，工作室表示「不认识」。',
        avatar:
          'https://readdy.ai/api/search-image?query=minimal%20flat%20vector%20portrait%20silhouette%20of%20a%20male%20figure%20with%20warm%20cream%20background%2C%20tomato%20red%20and%20amber%20tones%2C%20geometric%20shapes%2C%20no%20facial%20features%2C%20no%20text&width=400&height=400&seq=xigua-persona-male&orientation=squarish',
        tag: '主角',
      },
    ],
  },
  {
    id: 'm-03',
    title: '一张聊天记录截图流传三天，我们找到了它最早的版本',
    excerpt:
      '被大量转发的「实锤聊天记录」，其实在流传过程中被改动了两次。逐帧比对后，原始截图里的对话顺序完全不同。',
    category: '真伪鉴定',
    categorySlug: 'fact-check',
    tags: ['截图', '真伪', '实锤'],
    cover:
      'https://readdy.ai/api/search-image?query=a%20winding%20dotted%20timeline%20path%20with%20location%20pins%20and%20a%20watermelon%20marker%2C%20warm%20cream%20background%2C%20tomato%20red%20amber%20teal%20flat%20vector%20illustration%2C%20minimal%20magazine%20aesthetic%2C%20no%20text&width=800&height=600&seq=xigua-cover-03&orientation=landscape',
    author: '鉴定组 · 老白',
    date: '2026-09-06',
    readTime: '7 分钟',
    heat: 11250,
    status: '已完结',
    featured: true,
    verdict: '原始截图与流传版本存在明显差异，流传版本不足以作为证据。',
    verdictLevel: '已确认',
    content: [
      '这条瓜最有意思的地方在于，它看起来「证据齐全」，但越是完整的截图，越要警惕拼接的可能。',
      '我们将流传的三个版本统一到相同时区后比对，发现最广传的版本中，两条对话的先后顺序被调换了，而调换之后正好构成了「一句话承认关系」的观感。',
      '原始版本里，这两条消息相隔近六个小时，中间还有内容被删除。因此我们给出的结论是：截图本身可能为真，但流传版本经过了编辑，不能当作实锤。',
    ],
    timeline: [
      { time: '第 1 天 18:00', text: '小范围论坛出现第一版截图，内容零散。' },
      { time: '第 2 天 08:30', text: '拼接版截图出现，对话顺序被调换后迅速扩散。' },
      { time: '第 3 天 16:45', text: '比对结论发布，多数账号删除转发内容。' },
    ],
    personas: [
      {
        name: '当事人 · 女方',
        role: '歌手 / 女主',
        note: '工作室发布声明，称截图经过编辑，保留追责权利。',
        avatar:
          'https://readdy.ai/api/search-image?query=minimal%20flat%20vector%20portrait%20silhouette%20of%20a%20female%20figure%20with%20warm%20cream%20background%2C%20amber%20gold%20and%20deep%20teal%20tones%2C%20geometric%20shapes%2C%20no%20facial%20features%2C%20no%20text&width=400&height=400&seq=xigua-persona-female&orientation=squarish',
        tag: '女主',
      },
      {
        name: '首发账号',
        role: '匿名爆料号',
        note: '账号已注销，无法追溯来源。',
        avatar:
          'https://readdy.ai/api/search-image?query=minimal%20flat%20vector%20abstract%20avatar%20of%20an%20anonymous%20masked%20profile%20icon%2C%20warm%20cream%20background%2C%20tomato%20red%20and%20teal%20tones%2C%20geometric%20shapes%2C%20no%20text&width=400&height=400&seq=xigua-persona-anon&orientation=squarish',
        tag: '来源',
      },
    ],
  },
  {
    id: 'm-04',
    title: '剧组冲突传闻反转再反转，三个版本哪个更接近现场',
    excerpt:
      '同一段片场视频出现了三种不同解读。我们请做后期的朋友帮忙看了帧率与现场音，结果比想象中简单。',
    category: '事件追踪',
    categorySlug: 'event-track',
    tags: ['片场', '反转', '视频'],
    cover:
      'https://readdy.ai/api/search-image?query=an%20elegant%20theatre%20mask%20beside%20a%20round%20mirror%20reflecting%20a%20different%20shape%2C%20warm%20cream%20background%2C%20tomato%20red%20amber%20gold%20deep%20teal%20flat%20vector%20illustration%2C%20minimal%20geometric%20no%20text&width=800&height=600&seq=xigua-cover-04&orientation=landscape',
    author: '瓜田编辑部',
    date: '2026-09-05',
    readTime: '5 分钟',
    heat: 6890,
    status: '追踪中',
    verdict: '视频未经过变速剪辑，但现场收音缺失，双方口径仍有出入。',
    verdictLevel: '存疑',
    content: [
      '片场冲突类传闻的难点在于，画面往往只有几秒，解读空间却很大。',
      '我们对视频做了最简单的检查：前后帧率一致，没有变速；但环境音被替换成了配乐，因此无法从声音判断现场情绪。',
      '基于现有材料，可以确认视频没有经过明显剪辑，无法确认的是画外音到底说了什么，所以两个「反转版本」都缺少立足点。',
    ],
    timeline: [
      { time: '第 1 天 15:10', text: '现场路人视频流出，画面显示两人交谈。' },
      { time: '第 2 天 12:00', text: '自称剧组成员者发布「完整版」，加入配乐与字幕。' },
      { time: '第 3 天 09:30', text: '多个账号指出字幕与口型不符。' },
    ],
    personas: [
      {
        name: '当事人 · 男方',
        role: '演员 / 男主',
        note: '方发声明称仅为剧本讨论，态度正常。',
        avatar:
          'https://readdy.ai/api/search-image?query=minimal%20flat%20vector%20portrait%20silhouette%20of%20a%20male%20figure%20with%20warm%20cream%20background%2C%20tomato%20red%20and%20amber%20tones%2C%20geometric%20shapes%2C%20no%20facial%20features%2C%20no%20text&width=400&height=400&seq=xigua-persona-male&orientation=squarish',
        tag: '主角',
      },
    ],
  },
  {
    id: 'm-05',
    title: '热搜撤了就是「公关」吗？看懂热度曲线再下判断',
    excerpt:
      '热度回落不等于有人在压。我们把话题的每小时讨论量画成曲线，发现它其实和正常的自然衰减曲线高度接近。',
    category: '舆情风向',
    categorySlug: 'trend-watch',
    tags: ['热度', '曲线', '公关'],
    cover:
      'https://readdy.ai/api/search-image?query=vintage%20megaphone%20with%20radiating%20sound%20waves%20and%20scattered%20paper%20notes%2C%20warm%20cream%20background%2C%20tomato%20red%20amber%20teal%20flat%20vector%20illustration%2C%20minimal%20magazine%20style%2C%20no%20text&width=800&height=600&seq=xigua-cover-05&orientation=landscape',
    author: '舆情组 · 阿禾',
    date: '2026-09-04',
    readTime: '4 分钟',
    heat: 5230,
    status: '已完结',
    verdict: '讨论量回落符合自然衰减规律，暂无证据指向人为压制。',
    verdictLevel: '多方印证',
    content: [
      '「热搜掉了」几乎每次都会出现，但热度回落是正常的——大部分话题的生命周期只有两到三天。',
      '把每小时讨论量画成曲线后可以看到，这个话题在第二天晚间达到峰值，之后每小时下降约一成五，和前一周同量级话题的衰减速度基本一致。',
      '需要说明的是，我们没有平台内部数据，无法看到人工干预的部分，因此只能说「从公开曲线看没有异常」，而不是断言一定没有公关。',
    ],
    timeline: [
      { time: '第 1 天 20:00', text: '话题首次进入榜单前十。' },
      { time: '第 2 天 22:00', text: '讨论量达到峰值。' },
      { time: '第 3 天 18:00', text: '曲线进入自然衰减区间。' },
    ],
    personas: [
      {
        name: '话题本身',
        role: '舆情样本',
        note: '样本量约 42 万条公开讨论。',
        avatar:
          'https://readdy.ai/api/search-image?query=minimal%20flat%20vector%20abstract%20avatar%20of%20an%20anonymous%20masked%20profile%20icon%2C%20warm%20cream%20background%2C%20tomato%20red%20and%20teal%20tones%2C%20geometric%20shapes%2C%20no%20text&width=400&height=400&seq=xigua-persona-anon&orientation=squarish',
        tag: '数据',
      },
    ],
  },
  {
    id: 'm-06',
    title: '从「合作」到「同款」，品牌方是怎么被卷进这场瓜的',
    excerpt:
      '两个被指「同款」的单品，其实来自同一场品牌活动。追溯活动名单后，这条线索可以直接划掉。',
    category: '真伪鉴定',
    categorySlug: 'fact-check',
    tags: ['同款', '品牌', '误读'],
    cover:
      'https://readdy.ai/api/search-image?query=a%20curling%20watermelon%20vine%20wrapping%20around%20a%20small%20telescope%20pointed%20at%20stars%2C%20warm%20cream%20background%2C%20tomato%20red%20amber%20deep%20teal%20flat%20vector%20illustration%2C%20minimal%20geometric%2C%20no%20text&width=800&height=600&seq=xigua-cover-06&orientation=landscape',
    author: '鉴定组 · 小南',
    date: '2026-09-03',
    readTime: '4 分钟',
    heat: 4180,
    status: '已完结',
    verdict: '所谓同款来自同一场活动的统一赠礼，与私人关系无关。',
    verdictLevel: '已确认',
    content: [
      '「同款」是最好用也最容易翻车的线索：单品可以是巧合，也可以是活动统一发放。',
      '我们找到了该品牌当季活动的参与名单，两位当事人都在受邀之列，且活动结束后品牌方给每位嘉宾都寄了同系列礼盒。',
      '因此这条线索可以直接划掉。判断同款时，先问一句「这是不是活动/剧组的统一物料」，能省掉一大半误会。',
    ],
    timeline: [
      { time: '第 1 天 10:20', text: '网友发现两人佩戴同系列单品。' },
      { time: '第 2 天 14:00', text: '品牌活动名单被找出，两人同为受邀嘉宾。' },
      { time: '第 3 天 11:00', text: '线索标记为无效。' },
    ],
    personas: [
      {
        name: '品牌活动',
        role: '第三方来源',
        note: '活动为公开邀约，统一赠送同系列礼盒。',
        avatar:
          'https://readdy.ai/api/search-image?query=minimal%20flat%20vector%20abstract%20avatar%20of%20an%20anonymous%20masked%20profile%20icon%2C%20warm%20cream%20background%2C%20tomato%20red%20and%20teal%20tones%2C%20geometric%20shapes%2C%20no%20text&width=400&height=400&seq=xigua-persona-anon&orientation=squarish',
        tag: '来源',
      },
    ],
  },
  {
    id: 'm-07',
    title: '后援会声明与工作室声明对不上，粉丝内部先吵起来了',
    excerpt:
      '同一件事，后援会的说法和工作室的说法有出入。我们把两份声明逐句对照，分歧点其实只有一个。',
    category: '事件追踪',
    categorySlug: 'event-track',
    tags: ['声明', '后援会', '对照'],
    cover:
      'https://readdy.ai/api/search-image?query=stylized%20camera%20with%20a%20soft%20flash%20burst%20and%20floating%20photo%20frames%2C%20warm%20cream%20background%2C%20tomato%20red%20amber%20gold%20deep%20teal%20flat%20vector%20illustration%2C%20minimal%20magazine%20style%2C%20no%20text&width=800&height=600&seq=xigua-cover-07&orientation=landscape',
    author: '瓜田编辑部',
    date: '2026-09-02',
    readTime: '5 分钟',
    heat: 5970,
    status: '追踪中',
    verdict: '两份声明的核心分歧在于「是否知情」，其余表述基本一致。',
    verdictLevel: '多方印证',
    content: [
      '粉丝向组织与工作室的公开声明不一致，往往意味着信息在传递过程中出现了偏差，而不一定是对立。',
      '逐句对照后可以看到，两份声明对时间、地点的描述完全一致，唯一不同的是对「是否提前知情」的表述：一份说「事先不知」，一份说「已做沟通」。',
      '这个分歧目前无法从公开信息判断谁更准确。更值得关注的其实是，两份声明都回避了同一件事的具体细节。',
    ],
    timeline: [
      { time: '第 1 天 16:00', text: '后援会率先发布声明，强调事先不知情。' },
      { time: '第 1 天 20:30', text: '工作室声明称已与相关方做过沟通。' },
      { time: '第 2 天 09:00', text: '粉丝内部就两份声明的措辞展开讨论。' },
    ],
    personas: [
      {
        name: '当事人 · 男方',
        role: '演员 / 男主',
        note: '工作室于当晚发布书面声明。',
        avatar:
          'https://readdy.ai/api/search-image?query=minimal%20flat%20vector%20portrait%20silhouette%20of%20a%20male%20figure%20with%20warm%20cream%20background%2C%20tomato%20red%20and%20amber%20tones%2C%20geometric%20shapes%2C%20no%20facial%20features%2C%20no%20text&width=400&height=400&seq=xigua-persona-male&orientation=squarish',
        tag: '主角',
      },
    ],
  },
  {
    id: 'm-08',
    title: '被指「白月光」的那位，其实早就发过澄清了',
    excerpt:
      '一条三年前的旧澄清被重新翻出来，恰好能对上今天传闻里的时间点。旧材料怎么用，这里有个示范。',
    category: '人物档案',
    categorySlug: 'person-profile',
    tags: ['旧料', '澄清', '时间点'],
    cover:
      'https://readdy.ai/api/search-image?query=a%20balanced%20scale%20with%20a%20watermelon%20slice%20on%20each%20side%2C%20warm%20cream%20background%2C%20tomato%20red%20amber%20teal%20flat%20vector%20illustration%2C%20minimal%20geometric%20magazine%20aesthetic%2C%20no%20text&width=800&height=600&seq=xigua-cover-08&orientation=landscape',
    author: '档案组 · 林一',
    date: '2026-09-01',
    readTime: '5 分钟',
    heat: 4390,
    status: '已完结',
    verdict: '三年前的澄清与本次传闻的时间线可以对应，传闻的说法需要修正。',
    verdictLevel: '已确认',
    content: [
      '旧料不是不能用，关键是看它的时间点能不能对上现在的说法。',
      '这条瓜里，传闻称某位艺人「从未回应过」。但我们找到了三年前的一则澄清，发布时间恰好覆盖传闻所指的时段，只是当时热度不高，很少有人记得。',
      '把旧材料放回时间线，是吃瓜里最有价值的一步。它不制造新结论，但能直接排除掉一些看似成立的推断。',
    ],
    timeline: [
      { time: '三年前 19:20', text: '当事人发布澄清，说明当时的关系状态。' },
      { time: '第 1 天 13:00', text: '旧澄清被重新翻出，与传闻时间线对照。' },
      { time: '第 2 天 10:00', text: '档案更新为「已完结」。' },
    ],
    personas: [
      {
        name: '当事人 · 女方',
        role: '歌手 / 女主',
        note: '三年前曾在个人账号发布澄清内容。',
        avatar:
          'https://readdy.ai/api/search-image?query=minimal%20flat%20vector%20portrait%20silhouette%20of%20a%20female%20figure%20with%20warm%20cream%20background%2C%20amber%20gold%20and%20deep%20teal%20tones%2C%20geometric%20shapes%2C%20no%20facial%20features%2C%20no%20text&width=400&height=400&seq=xigua-persona-female&orientation=squarish',
        tag: '女主',
      },
    ],
  },
  {
    id: 'm-09',
    title: '吃瓜黑话速成：空降、实锤、白月光到底什么意思',
    excerpt:
      '刚开始吃瓜的人最容易在评论区迷路。这份小词典整理了二十个高频词，配上最近的真实语境，让你三分钟跟上节奏。',
    category: '吃瓜词典',
    categorySlug: 'glossary',
    tags: ['黑话', '入门', '词典'],
    cover:
      'https://readdy.ai/api/search-image?query=an%20open%20dictionary%20page%20with%20a%20small%20watermelon%20illustration%20and%20floating%20letters%2C%20warm%20cream%20background%2C%20tomato%20red%20amber%20deep%20teal%20flat%20vector%20illustration%2C%20minimal%20magazine%20style%2C%20no%20text&width=800&height=600&seq=xigua-cover-09&orientation=landscape',
    author: '词典组 · 阿禾',
    date: '2026-08-30',
    readTime: '3 分钟',
    heat: 3620,
    status: '已完结',
    verdict: '为入门读者整理，词语含义以当下主流用法为准。',
    verdictLevel: '多方印证',
    content: [
      '「空降」一般指某条内容在没有预热的情况下突然出现并登上热门，通常带有「有人推」的意味。',
      '「实锤」指能够直接证明结论的材料。要小心的是，很多被称为实锤的截图，其实只证明了「存在一段对话」，并不证明对话内容的真实性。',
      '「白月光」最早指求而不得的对象，现在在瓜圈里泛指被反复提起、却始终没有结果的旧人。理解语境比背词义更重要。',
    ],
    timeline: [
      { time: '第 1 天 12:00', text: '词典第一版上线，收录十二个词条。' },
      { time: '第 3 天 15:00', text: '根据读者留言补充至二十个词条。' },
    ],
    personas: [
      {
        name: '词典组',
        role: '内容团队',
        note: '词条含义参考当下主流用法整理。',
        avatar:
          'https://readdy.ai/api/search-image?query=minimal%20flat%20vector%20abstract%20avatar%20of%20an%20anonymous%20masked%20profile%20icon%2C%20warm%20cream%20background%2C%20tomato%20red%20and%20teal%20tones%2C%20geometric%20shapes%2C%20no%20text&width=400&height=400&seq=xigua-persona-anon&orientation=squarish',
        tag: '团队',
      },
    ],
  },
];

export const featuredStories = stories.filter((item) => item.featured);

export const getStoryById = (id: string): Story | undefined =>
  stories.find((item) => item.id === id);

export const getStoriesByCategory = (slug: string): Story[] =>
  stories.filter((item) => item.categorySlug === slug);

export const getRelatedStories = (story: Story, size = 3): Story[] =>
  stories
    .filter((item) => item.id !== story.id)
    .sort((a, b) => {
      const aScore = a.categorySlug === story.categorySlug ? 1 : 0;
      const bScore = b.categorySlug === story.categorySlug ? 1 : 0;
      return bScore - aScore || b.heat - a.heat;
    })
    .slice(0, size);